import React from 'react'
import {useField} from 'formik'
import {Eye, EyeOff} from 'lucide-react'
import {Button, Input} from '~/src/design-system/Primitives'
import {FieldHelper, FieldLabel, FieldWrapper} from '~/src/design-system-shims/FieldParts'

export interface FormikPasswordInputProps {
  name: string
  label: string
  type?: string
  InputLabelProps?: object
  'data-testid'?: string
  placeholder?: string
  autoComplete?: string
  iconLeft?: React.ReactNode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function FormikPasswordInput({
  name,
  label,
  InputLabelProps: _InputLabelProps,
  'data-testid': dataTestId,
  onChange: customOnChange,
  ...rest
}: FormikPasswordInputProps) {
  const [field, meta] = useField(name)

  const [showPassword, setShowPassword] = React.useState(false)

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev)
  }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const hasError = meta.touched && Boolean(meta.error)
  const errorText = hasError && meta.error !== 'DoNotShow' ? meta.error : undefined

  return (
    <FieldWrapper $fullWidth data-testid="password-formik-text-field">
      {label && <FieldLabel htmlFor={`password-field-${name}`}>{label}</FieldLabel>}
      <Input
        id={`password-field-${name}`}
        {...field}
        {...rest}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          customOnChange?.(e)
          field.onChange(e)
        }}
        type={showPassword ? 'text' : 'password'}
        error={hasError}
        aria-describedby={errorText ? `password-field-${name}-error` : undefined}
        iconRight={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            data-testid="password-visibility-toggle"
            iconLeft={showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
          />
        }
      />
      {errorText && (
        <FieldHelper id={`password-field-${name}-error`} $error role="alert">
          {errorText}
        </FieldHelper>
      )}
    </FieldWrapper>
  )
}

export default FormikPasswordInput
